<script setup lang="ts">
import { computed, useId } from 'vue'

/**
 * 具備標籤、錯誤提示與無障礙屬性的輸入框。
 *
 * 錯誤訊息由父層以「已翻譯的字串」傳入 —— 這個元件不查 i18n，
 * 保持純展示，才能被任何情境重複使用。
 */
const props = withDefaults(
  defineProps<{
    label: string
    type?: 'text' | 'password' | 'tel' | 'email' | 'date'
    placeholder?: string
    error?: string | null
    hint?: string | null
    autocomplete?: string
    // decimal 是給金額用的 —— numeric 的手機鍵盤沒有小數點
    inputmode?: 'text' | 'numeric' | 'decimal'
    /** 沒有預設值 —— exactOptionalPropertyTypes 下不能用 undefined 當 default */
    maxlength?: number
    disabled?: boolean
  }>(),
  {
    type: 'text',
    placeholder: '',
    error: null,
    hint: null,
    autocomplete: 'off',
    inputmode: 'text',
    disabled: false,
  },
)

const model = defineModel<string>({ required: true })

const id = useId()
const errorId = computed(() => `${id}-error`)
const hintId = computed(() => `${id}-hint`)

// 只把實際存在的描述元素接上，避免指向不存在的 id
const describedBy = computed(() => {
  const ids: string[] = []
  if (props.error !== null) ids.push(errorId.value)
  else if (props.hint !== null) ids.push(hintId.value)
  return ids.length > 0 ? ids.join(' ') : undefined
})
</script>

<template>
  <div class="base-input">
    <label :for="id" class="base-input__label">{{ label }}</label>

    <input
      :id="id"
      v-model="model"
      class="base-input__control"
      :class="{ 'base-input__control--invalid': error !== null }"
      :type="type"
      :placeholder="placeholder"
      :autocomplete="autocomplete"
      :inputmode="inputmode"
      :maxlength="maxlength"
      :disabled="disabled"
      :aria-invalid="error !== null"
      :aria-describedby="describedBy"
    />

    <p v-if="error !== null" :id="errorId" class="base-input__error" role="alert">
      {{ error }}
    </p>
    <p v-else-if="hint !== null" :id="hintId" class="base-input__hint">
      {{ hint }}
    </p>
  </div>
</template>

<style scoped lang="scss">
.base-input {
  display: flex;
  flex-direction: column;
  gap: $space-2;

  &__label {
    font-size: $text-sm;
    color: $color-text-muted;
  }

  &__control {
    width: 100%;
    padding: $space-3 $space-4;
    // 在對話框／登入卡裡走比較亮的面板色，其他地方沿用全域深色。
    // 變數由 `panel-surface` mixin 掛在面板上，這裡只負責繼承
    background: var(--panel-input, #{$color-surface-input});
    border: 1px solid var(--panel-border, #{$color-border});
    border-radius: $radius-md;
    color: $color-text;
    font-size: $text-base;
    // 讓 type="date" 的原生日曆圖示與彈出面板跟著深色主題走。
    // 少了它，Chrome 會畫一個黑色圖示在深色底上，等於看不見。
    color-scheme: dark;

    &::placeholder {
      color: rgba($color-text-muted, 0.7);
    }

    &:focus {
      outline: 2px solid $color-action;
      outline-offset: 1px;
    }

    &:disabled {
      opacity: 0.6;
    }

    &--invalid {
      border-color: $color-danger;
    }
  }

  &__error {
    margin: 0;
    color: $color-danger;
    font-size: $text-sm;
  }

  &__hint {
    margin: 0;
    color: $color-text-muted;
    font-size: $text-xs;
  }
}
</style>
